import { useEffect } from "react";
import { faTag } from "@fortawesome/pro-thin-svg-icons";

import Icon from "common/components/Icon";
import { WithStringValues } from "common/utils/types";
import { Feature } from "common/types";
import { useListSearchState } from "common/hooks";

import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import Results from "client/common/components/results/Results";
import { ProductListSource } from "client/analytics/types";
import { useFeatures } from "client/clinics/hooks";
import { Paths } from "client/routes";

import { useFilterPanel, useProductDealsSearchQuery, useProductFilterOptionState } from "../hooks";
import { PRODUCT_DEALS_FILTER_SCOPE } from "../hooks/constants";
import {
  PRODUCT_FILTER_DEFINITIONS,
  PRODUCT_LIST_PAGE_SIZE,
  PRODUCT_LIST_PAGE_SIZE_WITH_PANEL,
} from "../constants/filterDefinitions";
import { getProductFiltersFromSearch } from "../utils";
import { ProductDealsSearch, ProductSearchFilters, SelectedProductFilters } from "../types";
import ProductCardList from "./ProductCardList";
import ProductCardListHeader from "./ProductCardListHeader";
import AppliedFilters from "./AppliedFilters";
import FilterLayout from "./FilterLayout";
import FilterPanel from "./FilterPanel";
import AppliedFilterTags from "./AppliedFilterTags";

import styles from "../styles/DealsPage.module.less";

const initialSearch = {
  category_ids: [],
  manufacturer_ids: [],
  page: 1,
  page_size: PRODUCT_LIST_PAGE_SIZE,
  prescription_required: [],
  sort: "",
  species: [],
};

const initialSearchWithPanel = {
  ...initialSearch,
  page_size: PRODUCT_LIST_PAGE_SIZE_WITH_PANEL,
};

type DealsResultsProps = {
  search: WithStringValues<ProductDealsSearch>;
  onUpdateSearch: (search: Partial<ProductDealsSearch>) => void;
};

const DealsResults = ({ search, onUpdateSearch }: DealsResultsProps) => {
  const { data: products, isLoading, error, isRefetching } = useProductDealsSearchQuery(search);
  const { data: filterOptionState } = useProductFilterOptionState(PRODUCT_DEALS_FILTER_SCOPE);

  const filters = getProductFiltersFromSearch({ ...search, q: "" });

  const { filterOptions, hasCompleteOptions, showFilterPanel, hasSelectedFilters, clearAll } =
    useFilterPanel<SelectedProductFilters, ProductSearchFilters>({
      definitions: PRODUCT_FILTER_DEFINITIONS,
      responseFilterOptions: products?.filters,
      cachedFilterOptions: filterOptionState?.filters,
      responseHasCompleteOptions: products?.has_complete_filter_options,
      filters,
      onChange: onUpdateSearch,
    });

  if (isLoading) {
    return <Results.Loading description="Loading deals" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="deal" />;
  }

  const renderFilterPanel = () => {
    if (!showFilterPanel || !filterOptions) {
      return null;
    }
    return (
      <FilterPanel<SelectedProductFilters, ProductSearchFilters>
        definitions={PRODUCT_FILTER_DEFINITIONS}
        filterOptions={filterOptions}
        filters={filters}
        onChange={onUpdateSearch}
        hasSelectedFilters={hasSelectedFilters}
        onClearAll={clearAll}
      />
    );
  };

  const emptyDescription =
    hasCompleteOptions && hasSelectedFilters
      ? "No deals match the filters you have applied."
      : "Deals change often. Check back soon for new eligible savings.";

  return (
    <FilterLayout panel={renderFilterPanel()}>
      <div className={styles.container}>
        <ProductCardListHeader
          paginator={products.paginator}
          isRefetching={isRefetching}
          entityName="deal"
          filterOptions={filterOptions}
          filters={filters}
          onChange={onUpdateSearch}
          showFilters={!showFilterPanel}
          hasCompleteOptions={hasCompleteOptions}
        />
        {showFilterPanel && filterOptions && (
          <AppliedFilters hasSelectedFilters={hasSelectedFilters} onClearAll={clearAll}>
            <AppliedFilterTags<SelectedProductFilters, ProductSearchFilters>
              definitions={PRODUCT_FILTER_DEFINITIONS}
              options={filterOptions}
              value={filters}
              onChange={onUpdateSearch}
              variant="accent"
            />
          </AppliedFilters>
        )}
        {products.results.length ? (
          <ProductCardList
            results={products.results}
            listSource={ProductListSource.DealsPage}
            listContext={{ numResults: products.paginator.count }}
            paginator={products.paginator}
            onPageChange={(page) => onUpdateSearch({ page })}
            entityName="deal"
            isNarrow={showFilterPanel}
          />
        ) : (
          <Results.None header="No deals found" description={emptyDescription} />
        )}
      </div>
    </FilterLayout>
  );
};

const DealsPage = () => {
  const isFiltersV2 = useFeatures()[Feature.StorefrontFiltersV2];
  const [search, updateSearch] = useListSearchState<Paths["products"]["deals"]>(
    isFiltersV2 ? initialSearchWithPanel : initialSearch,
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  return (
    <Page className={styles.page}>
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>Deals</h1>
              <div className={styles.description}>
                Save on your pet&apos;s care with autoship and manufacturer discounts, plus
                limited-time offers, all in one place.
              </div>
            </div>
            <Icon className={styles.icon} icon={faTag} />
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <DealsResults search={search} onUpdateSearch={updateSearch} />
          </Body>
        </div>
      </div>
    </Page>
  );
};

export default DealsPage;
